"use client";

import React, { createContext, useContext, useState, useEffect } from 'react';

export type UnitType = 'g' | 'oz' | 'kg' | 'lb';

interface UnitContextType {
    activeUnit: UnitType;
    setActiveUnit: (unit: UnitType) => void;
    convertValue: (value: number, from: UnitType, to: UnitType) => number;
    formatValue: (value: number, unit?: UnitType) => string;
}

const CONVERSION_RATES: Record<UnitType, number> = {
    g: 1,
    oz: 28.3495,
    kg: 1000,
    lb: 453.592,
};

const UnitContext = createContext<UnitContextType | undefined>(undefined);

export const UnitProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [activeUnit, setActiveUnitState] = useState<UnitType>('g');

    // Load from localStorage
    useEffect(() => {
        const savedUnit = localStorage.getItem('sb-preferred-unit') as UnitType;
        if (savedUnit && ['g', 'oz', 'kg', 'lb'].includes(savedUnit)) {
            setActiveUnitState(savedUnit);
        }
    }, []);

    const setActiveUnit = (unit: UnitType) => {
        setActiveUnitState(unit);
        localStorage.setItem('sb-preferred-unit', unit);
    };

    const convertValue = (value: number, from: UnitType, to: UnitType): number => {
        if (from === to) return value;
        const valueInGrams = value * CONVERSION_RATES[from];
        return valueInGrams / CONVERSION_RATES[to];
    };

    const formatValue = (value: number, unit: UnitType = activeUnit): string => {
        const decimals = (unit === 'kg' || unit === 'lb') ? 3 : 1;
        return value.toLocaleString(undefined, {
            minimumFractionDigits: 0,
            maximumFractionDigits: decimals,
        });
    };

    return (
        <UnitContext.Provider value={{ activeUnit, setActiveUnit, convertValue, formatValue }}>
            {children}
        </UnitContext.Provider>
    );
};

export const useUnits = () => {
    const context = useContext(UnitContext);
    if (context === undefined) {
        throw new Error('useUnits must be used within a UnitProvider');
    }
    return context;
};
